import { useEffect } from 'react';

import { useRewardTrackStore } from '@/entities/reward-track';
import type { Encounter } from '@/entities/reward-track';
import { RADIUS, Skeleton, Squircle } from '@/shared/ui';

import { encounterSubtitle, formatReward } from '../model/encounter-text';
import { useChapterCountdown } from '../model/use-chapter-countdown';

import styles from './CareerTrack.module.scss';

/** Лента наград / «Карьера» (ГДД §8, узел 438:51622). */
export function CareerTrack() {
  const track = useRewardTrackStore((state) => state.track);
  const isLoading = useRewardTrackStore((state) => state.isLoading);
  const error = useRewardTrackStore((state) => state.error);
  const load = useRewardTrackStore((state) => state.load);
  const countdown = useChapterCountdown(track?.nextChapterStartsAt ?? null);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading && !track) {
    return <Skeleton count={5} height={171} />;
  }

  if (error) {
    return (
      <p className={styles.error} role="alert" data-testid="career-error">
        {error}
      </p>
    );
  }

  if (!track) return null;

  return (
    <div className={styles.page} data-testid="career-track">
      <Squircle cornerRadius={RADIUS.xl} className={styles.chapter}>
        <h2 className={styles.chapterTitle}>Глава {track.chapterIndex}</h2>

        <ul className={styles.cards}>
          {track.claimed.map((encounter) => (
            <li key={encounter.id}>
              <CupCard encounter={encounter} points={track.points} claimed />
            </li>
          ))}
          {track.upcoming.map((encounter) => (
            <li key={encounter.id}>
              <CupCard encounter={encounter} points={track.points} claimed={false} />
            </li>
          ))}
        </ul>
      </Squircle>

      {/* Последняя Глава сезона: следующей нет, отсчитывать нечего.
          Копирайта для этого состояния в макете нет — блок скрывается,
          а не заполняется выдумкой (пункт в backlog.md). */}
      {countdown ? (
        <Squircle cornerRadius={RADIUS.xl} className={styles.footer}>
          <p className={styles.footerText}>
            Глава {track.chapterIndex + 1} начнётся через{' '}
            <span className={styles.footerCountdown} data-testid="career-countdown">
              {countdown}
            </span>
          </p>
        </Squircle>
      ) : null}
    </div>
  );
}

function CupCard({
  encounter,
  points,
  claimed,
}: {
  encounter: Encounter;
  points: number;
  claimed: boolean;
}) {
  const progress = Math.min(points, encounter.pointsThreshold);
  const subtitle = encounterSubtitle(encounter);

  return (
    <Squircle
      cornerRadius={RADIUS.xl}
      className={claimed ? styles.cardClaimed : styles.card}
      data-testid={claimed ? 'career-card-claimed' : 'career-card-upcoming'}
      // Название ступени в подсказку, а не в заголовок: заголовок занят
      // номиналом. Атрибут валиден и ничего не ломает, но полагаться
      // на него нельзя — в мобильном клиенте Telegram наведения нет,
      // и подсказку там не увидит никто. Это способ не потерять поле,
      // а не способ его показать.
      title={encounter.title}
    >
      <span className={styles.reward}>
        <img
          className={styles.rewardIcon}
          src="/images/header/predict-coin.svg"
          alt=""
          aria-hidden
        />
        {formatReward(encounter)}
      </span>

      {/* Подпись всегда собирается из порога и номинала, середина
          выделена розовым (узел 438:51635) — одинаково у пройденных
          и предстоящих Энкаунтеров.

          `description` с сервера здесь НЕ показывается: выделить в нём
          нужную часть нельзя (произвольный текст), а карточка без
          выделения выбивалась бы из ряда. Поле остаётся в контракте
          и пустует на живых данных — сид его не заполняет. */}
      <p className={styles.description}>
        {subtitle.before}
        <span className={styles.descriptionAccent}>{subtitle.highlight}</span>
        {subtitle.after}
      </p>

      <div className={styles.foot}>
        <span className={styles.progress}>
          <img
            className={styles.progressIcon}
            src="/images/header/fire.svg"
            alt=""
            aria-hidden
          />
          {progress} из {encounter.pointsThreshold}
        </span>
        {claimed ? <span className={styles.done}>готово</span> : null}
      </div>
    </Squircle>
  );
}
